import { Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { mainColors } from '../../helpers/colors';
import {
  deleteNftActionRequest,
  setNftsSortDataAction,
  sortNftsAction,
} from '../../store/nft/nftActions';
import { setAlertAction } from '../../store/alert/alertActions';
import useMercurxPagination from '../../hooks/useMercurxPagination';
import MercurXIcons from '../MercurXIcons/MercurXIcons';
import MercurXSortableHeader from '../MercurXSortableHeader/MercurXSortableHeader';
import MercurXTablePagination from '../MercurXTablePagination/MercurXTablePagination';
import { NFTS_SORT_TYPES, sortKeys } from '../../pages/NFTs/nftsConstants';
import './MercurXNftTable.scss';

function MercurXNftTable(props) {
  const {
    deleteNftRequest,
    setAlert,
    nfts,
    filteredNfts,
    nftsSortData,
    setNftsSortData,
    sortNfts,
  } = props;
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user'))?.user;
  const sourceData = Array.isArray(filteredNfts)
    ? filteredNfts
    : Array.isArray(nfts)
      ? nfts
      : [];
  const visibleNfts = sourceData.filter((item) => item?.status !== false);
  const {
    currentPage,
    totalPages,
    visiblePages,
    pagedData: renderedNfts,
    rowOffset,
    isFirstPage,
    isLastPage,
    goToPage,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  } = useMercurxPagination(visibleNfts, 10, 5);

  function scrollToNftsContainer() {
    const nftsContainer = document.getElementById('nfts-container');
    if (nftsContainer) {
      nftsContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const handlePageChange = (page) => {
    goToPage(page);
    scrollToNftsContainer();
  };

  const handleFirstPage = () => {
    goToFirstPage();
    scrollToNftsContainer();
  };

  const handlePrevPage = () => {
    goToPrevPage();
    scrollToNftsContainer();
  };

  const handleNextPage = () => {
    goToNextPage();
    scrollToNftsContainer();
  };

  const handleLastPage = () => {
    goToLastPage();
    scrollToNftsContainer();
  };

  const handleSort = (sortKey, defaultSortType) => {
    const nextSortType = nftsSortData.sortKey === sortKey
      ? (nftsSortData.sortType === NFTS_SORT_TYPES.ASCENDING
        ? NFTS_SORT_TYPES.DESCENDING
        : NFTS_SORT_TYPES.ASCENDING)
      : defaultSortType;

    setNftsSortData({
      sortKey,
      sortType: nextSortType,
    });
    sortNfts();
  };

  function editNft(nft) {
    history.push({
      pathname: '/nfts/edit-nft',
      state: {
        nft,
      },
    });
  }

  function deleteNft(nftID, nftIndex) {
    Swal.fire({
      icon: 'warning',
      iconColor: mainColors.warning,
      text: 'This process can not be undo. Are you sure?',
      showCancelButton: true,
    }).then((control) => {
      if (control.isConfirmed) {
        deleteNftRequest({
          nftID,
          nftIndex,
        });
      }
    });
  }

  function shortenText(value, startLength = 8, endLength = 6) {
    if (!value) {
      return '-';
    }

    const text = String(value);
    if (text.length <= startLength + endLength + 3) {
      return text;
    }

    return `${text.slice(0, startLength)}...${text.slice(-endLength)}`;
  }

  async function copyToClipboard(text, label = 'Value') {
    if (!text || text === '-') {
      return;
    }

    const normalizedText = String(text);
    let isCopied = false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedText);
        isCopied = true;
      }
    } catch (error) {
      // fallback below
    }

    if (!isCopied) {
      const textArea = document.createElement('textarea');
      textArea.value = normalizedText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      isCopied = document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    if (isCopied) {
      setAlert({
        title: 'Success!',
        text: `${label} Copied To Clipboard`,
        variant: 'success',
        outTimeMS: 3000,
      });
    }
  }

  function renderCopyableText(value, options = {}) {
    const { startLength = 8, endLength = 6, label = 'Value' } = options;
    if (!value) {
      return '-';
    }

    const text = String(value);
    return (
      <span
        role="button"
        tabIndex={0}
        className="copyable-cell-text"
        title={`${text} (Click to copy)`}
        onClick={() => copyToClipboard(text, label)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            copyToClipboard(text, label);
          }
        }}
      >
        {shortenText(text, startLength, endLength)}
      </span>
    );
  }

  function formatNumber(value) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    const formatBalance = (Math.floor((Number(value) / 10000) * 100) / 100).toString().split('.');
    const firstValueFormatBalance = Number(formatBalance[0]).toLocaleString('tr-TR');
    const secondValueFormatBalance = formatBalance[1] || '0';
    return `${firstValueFormatBalance},${secondValueFormatBalance}`;
  }

  function formatUsd(value) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    return Number(value).toLocaleString('tr-TR', {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'symbol',
      useGrouping: true,
      minimumFractionDigits: 4,
    });
  }

  return (
    <>
      <Table className="nfts-table-design" responsive hover borderless>
        <thead>
          <tr className="text-t-body-color text-fs-tag">
            <th>#</th>
            {sortKeys.map((sortKeyConfig, index) => (
              <th key={sortKeyConfig.name}>
                <MercurXSortableHeader
                  label={sortKeyConfig.name}
                  isActive={nftsSortData.sortKey === index}
                  isAscending={nftsSortData.sortType === NFTS_SORT_TYPES.ASCENDING}
                  onClick={() => handleSort(index, sortKeyConfig.defaultSortType)}
                />
              </th>
            ))}
            {user?.is_superuser && (
              <>
                <th />
                <th />
              </>
            )}
          </tr>
        </thead>
        {renderedNfts?.length ? (
          <tbody>
            {renderedNfts.map((item, index) => {
              const contractAddress =
                item?.nft_contract?.contract_address || item?.contract_address;
              const contractOwnerAddress =
                item?.nft_contract?.contract_owner_address || item?.contract_owner_address;

              return (
                <tr className="text-t-head-color" key={item.id || index}>
                  <td>{rowOffset + index + 1}</td>
                  <td>{item.name || '-'}</td>
                  <td>
                    {renderCopyableText(item.url, {
                      startLength: 22,
                      endLength: 14,
                      label: 'NFT URL',
                    })}
                  </td>
                  <td>{renderCopyableText(item.address, { label: 'NFT Address' })}</td>
                  <td>{renderCopyableText(item.owner_address, { label: 'NFT Owner Address' })}</td>
                  <td>{renderCopyableText(contractAddress, { label: 'Contract Address' })}</td>
                  <td>
                    {renderCopyableText(contractOwnerAddress, { label: 'Contract Owner Address' })}
                  </td>
                  <td>{formatNumber(item.total_supply)}</td>
                  <td>{item.price_in_mercurx ?? '-'}</td>
                  <td>{formatUsd(item.price_in_usd)}</td>
                  <td>{formatNumber(item.distribution)}</td>
                  <td>{item.current_price ?? '-'}</td>
                  <td>{item.all_time_high ?? '-'}</td>
                  {user?.is_superuser && (
                    <>
                      <td>
                        <div className="cursor-pointer mt-1" onClick={() => editNft(item)}>
                          <MercurXIcons
                            name="BsPencilSquare"
                            size={18}
                            color={mainColors.primary}
                          />
                        </div>
                      </td>
                      <td>
                        <div
                          className="cursor-pointer mt-1"
                          onClick={() => deleteNft(item.id, index)}
                        >
                          <MercurXIcons name="BsTrash3" size={18} color={mainColors.danger} />
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        ) : null}
      </Table>

      {!renderedNfts?.length && (
        <div className="d-flex text-fs-body-md text-t-body-color justify-content-center">
          No NFT found according to search results.
        </div>
      )}

      <MercurXTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        onFirstPage={handleFirstPage}
        onPrevPage={handlePrevPage}
        onPageChange={handlePageChange}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    nfts: state.nftReducer.nfts,
    filteredNfts: state.nftReducer.filteredNfts,
    nftsSortData: state.nftReducer.nftsSortData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteNftRequest: (payload) => {
      dispatch(deleteNftActionRequest(payload));
    },
    setNftsSortData: (payload) => {
      dispatch(setNftsSortDataAction(payload));
    },
    sortNfts: (payload) => {
      dispatch(sortNftsAction(payload));
    },
    setAlert: (payload) => {
      dispatch(setAlertAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MercurXNftTable);
